/**
 * mcpbVideoInfo — pure formatting for the cog "Camera Info overlay": the on-video VIDEO/STREAM-info
 * diagnostic, i.e. the dview-DESKTOP DstreamView "Toggle Info" (Res / FPS / Bitrate) equivalent
 * (plan §15 #22). NOT the cam name/timestamp label (that is fLiveOverlay/fOverlayBackground in
 * Setup → System General).
 *
 * Honest web scope for a recorded MP4 played through <video> (David 2026-06-21): we can read the
 * decoded resolution (videoWidth x videoHeight) and the readyState directly, estimate FPS from
 * requestVideoFrameCallback where the browser supports it, and report the transcode PROFILE we asked
 * pbserver for (profile=2 → 480p down-res at conf>1; profile=1 → native). Exact kbps bitrate is NOT
 * reliably exposed for an MP4 in-browser, so — unlike the desktop's "Bitrate: Nkbps" — we show the
 * profile instead of inventing a number. The DOM reads + rVFC live in hooks/useVideoInfo; everything
 * here is pure (unit-tested) so that hook stays a thin, headed-verified shim (jsdom has no media pipeline).
 */

// HTMLMediaElement.readyState → a short buffer label (0 HAVE_NOTHING … 4 HAVE_ENOUGH_DATA).
export const READY_LABELS = { 0: 'none', 1: 'none', 2: 'low', 3: 'ok', 4: 'ready' };

export const readyStateLabel = (rs) => (READY_LABELS[rs] != null ? READY_LABELS[rs] : 'none');

// The transcode profile we REQUESTED (NOT a measured bitrate — see the file header). Used only as the
// fallback before the decoded resolution is known.
export const profileLabel = (profile) => (Number(profile) > 1 ? '480p / down-res' : 'Native');

// pbserver's profile=2 target height (transcode.c TARGET_HEIGHT); anything at/under this is down-res'd.
const DOWNRES_MAX_HEIGHT = 540;

// Down-res vs native — derived from the ACTUAL decoded height when known, so the label never contradicts
// the resolution line on a backend that ignores our profile request (dvs-6.2 serves native even when
// conf>1 asks profile=2 — the 97aa5b63a finding; `videoHeight` then reports the native height). Falls
// back to the REQUESTED profile before metadata loads (height 0).
export const streamProfileLabel = ({ height = 0, profile = 1 }) => {
  if (height > 0) return height <= DOWNRES_MAX_HEIGHT ? '480p / down-res' : 'Native';
  return profileLabel(profile);
};

// Rolling FPS estimate from instantaneous samples (each = 1000/Δt between presented frames). Returns
// null for an empty / missing set (paused, or no rVFC) so the overlay shows "— fps", not a fake 0.
export const averageFps = (samples) => {
  if (!samples || !samples.length) return null;
  const sum = samples.reduce((a, b) => a + (Number(b) || 0), 0);
  return sum / samples.length;
};

// Build the stacked overlay lines from a video-info snapshot. Mirrors the desktop's 3-line block
// (fps / resolution / [bitrate→]profile) plus a buffer-state line. Tolerant of missing metadata (shows
// "—") so the overlay renders cleanly the instant it turns on, before the first frame paints.
export const formatVideoInfo = (info) => {
  if (!info) return [];
  const {
    width = 0, height = 0, fps = null, readyState = 0, profile = 1,
  } = info;
  const pro = Number(profile) || 1;
  return [
    fps != null && fps > 0 ? `~${Math.round(fps)} fps` : '— fps',
    width && height ? `${width}x${height}` : '—',
    // The explicit profile NUMBER (David #86bbmxrtt — the overlay showed only the quality label, not
    // which profile). Cloud: 2 = the lighter pro2 twin, 1 = the main clip (reflects a pro2→pro1
    // fallback). Local: the pbserver down-res profile.
    `Profile ${pro}`,
    // The quality label on its OWN line (David 2026-08-28).
    streamProfileLabel({ height, profile }),
    `buffer: ${readyStateLabel(readyState)}`,
  ];
};
